import React, { useState } from 'react';
import { graphql, Link } from 'gatsby';
import Image from 'gatsby-image';
import { uniq } from 'lodash';
import Layout from 'components/Layout';
import Button from 'components/Button';
import SEO from 'components/SEO';
import Tag from 'components/Tag';

export const results = graphql`
  {
    allAppsJson {
      edges {
        node {
          id
          title
          tags
          slug
          image {
            childImageSharp {
              fluid(maxWidth: 1920, quality: 100) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }
  }
`;

interface IProjects {
  allAppsJson: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        tags: [];
        slug: string;
        image: {
          childImageSharp: {
            fluid: {
              aspectRatio: number;
              base64: string;
              sizes: string;
              src: string;
              srcSet: string;
            };
          };
        };
      };
    }>;
  };
}

const Projects = ({ data, location }: { data: IProjects; location: Location }) => {
  const { edges: projects } = data.allAppsJson;
  const [tags] = useState(uniq<string>(projects.flatMap(({ node: { tags } }) => tags)));
  const [currentTags, setCurrentTags] = useState<string[]>([]);

  return (
    <Layout location={location}>
      <SEO title='Projects' />
      <div className='col-span-12 bg-white border-.1 border-solid border-gray-200 shadow-md rounded-xl p-3 flex flex-wrap gap-2'>
        <p className='font-sans font-semibold text-2xl w-full'>Selected Tags:</p>
        {tags.map((tag, index) => {
          const isInArray = currentTags.includes(tag);
          return (
            <span
              key={tag + index}
              className={`col-span-1 text-white ${
                isInArray ? 'bg-gray-300' : 'bg-accent'
              } rounded-full text-center w-max px-4 py-1 cursor-pointer hover:bg-gray-500`}
              onClick={() =>
                setCurrentTags((prevTags) => {
                  if (isInArray) {
                    return [...prevTags.filter((selectedTag) => tag !== selectedTag)];
                  }
                  return [...prevTags, tag];
                })
              }
            >
              {tag}
            </span>
          );
        })}
      </div>
      <div className='col-span-12 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-5'>
        {projects.map(({ node: { id, title, tags, slug, image } }) => {
          const isInArray = currentTags.length !== 0 ? tags.find((tag) => currentTags.includes(tag)) : true;
          return isInArray ? (
            <Link
              key={id}
              to={`/projects/${slug}`}
              className='grid grid-cols-2 bg-white col-span-1 border-.1 border-solid border-gray-200 shadow-md rounded-xl transition-shadow p-3 hover:shadow-xl'
            >
              {image?.childImageSharp?.fluid ? (
                <Image fluid={image.childImageSharp.fluid} className='col-span-2 object-contain max-h-64 rounded-lg' />
              ) : (
                'There are no image'
              )}
              <div className='min-h-24 min-h-36 col-span-2'>
                <p className='my-2 font-sans text-2xl font-bold text-accent'>{title}</p>
                <div className='flex flex-wrap gap-1'>
                  {tags.map((tag, index) => (
                    <Tag tag={tag} key={index + tag} />
                  ))}
                </div>
              </div>
            </Link>
          ) : null;
        })}
      </div>
      <div className='col-span-12 flex justify-center mt-5'>
        <Button callback={() => scrollTo({ top: 0, behavior: 'smooth' })} icon='bx-chevron-up' innerText='Go to page top' />
      </div>
    </Layout>
  );
};

export default Projects;
